import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Header } from '@/components/layout/Header'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useGroups } from '@/hooks/useGroups'
import { useLeagues } from '@/hooks/useLeagues'
import { useProfile } from '@/hooks/useProfile'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { groupService, type BankDetails, type GroupType } from '@/services/groupService'
import { leagueService } from '@/services/leagueService'
import { imageFileToSquarePngDataUrl } from '@/utils/image'
import { ArrowLeft, Settings, AlertCircle, Loader2, Users, Trophy, Mail, Landmark, UserPlus } from 'lucide-react'
import { ConfirmDeleteDialog } from '@/components/ui/confirm-delete-dialog'

// Import refactored components
import {
  SettingsTab,
  LeaguesTab,
  MembersTab,
  InvitesTab,
  PaymentsTab,
  JoinRequestsTab,
  CreateLeagueDialog,
  DeleteGroupDialog,
  type GroupMember,
  type GroupInvite,
  type LeagueFormat,
  type MatchFormat,
} from '@/components/group-admin'

const VALID_TABS = ['settings', 'leagues', 'members', 'invites', 'requests', 'payments'] as const
type TabValue = typeof VALID_TABS[number]

export function GroupAdmin() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { groups, isLoading, refetch } = useGroups()
  const { leagues, isLoading: leaguesLoading, error: leaguesError, refetch: refetchLeagues } = useLeagues(id)
  const { profile } = useProfile()
  
  // Members state
  const [members, setMembers] = useState<GroupMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null)
  const [updatingOrganiserId, setUpdatingOrganiserId] = useState<string | null>(null)
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null)
  const [removeMemberDialog, setRemoveMemberDialog] = useState<GroupMember | null>(null)
  const [removeMemberError, setRemoveMemberError] = useState<string | null>(null)

  // Invites state
  const [invites, setInvites] = useState<GroupInvite[]>([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const [invitesError, setInvitesError] = useState<string | null>(null)
  const [createInviteEmail, setCreateInviteEmail] = useState('')
  const [createInviteRole, setCreateInviteRole] = useState<'member' | 'admin'>('member')
  const [createInviteLoading, setCreateInviteLoading] = useState(false)
  const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null)
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false)
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null)
  
  // Leagues state
  const [createLeagueDialogOpen, setCreateLeagueDialogOpen] = useState(false)
  const [leagueFormName, setLeagueFormName] = useState('')
  const [leagueFormFormat, setLeagueFormFormat] = useState<LeagueFormat>('singles')
  const [leagueFormMatchFormat, setLeagueFormMatchFormat] = useState<MatchFormat>('bestOf1')
  const [leagueFormDescription, setLeagueFormDescription] = useState('')
  const [leagueFormSaving, setLeagueFormSaving] = useState(false)
  const [leagueFormError, setLeagueFormError] = useState<string | null>(null)
  
  // Settings form state
  const [settingsName, setSettingsName] = useState('')
  const [settingsIcon, setSettingsIcon] = useState<string | null>(null)
  const [settingsGroupType, setSettingsGroupType] = useState<GroupType>('club')
  const [settingsShowOrganisers, setSettingsShowOrganisers] = useState(true)
  const [settingsIsPublic, setSettingsIsPublic] = useState(false)
  const [settingsIsFeatured, setSettingsIsFeatured] = useState(false)
  const [settingsAllowJoinRequests, setSettingsAllowJoinRequests] = useState(true)
  const [hasNewIcon, setHasNewIcon] = useState(false)
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [settingsSuccess, setSettingsSuccess] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)
  
  // Payments form state
  const [settingsBankDetails, setSettingsBankDetails] = useState<BankDetails>({})
  const [paymentsSaving, setPaymentsSaving] = useState(false)
  const [paymentsError, setPaymentsError] = useState<string | null>(null)
  const [paymentsSuccess, setPaymentsSuccess] = useState(false)
  
  // Delete group state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Get active tab from URL hash
  const activeTab = useMemo(() => {
    const hash = location.hash.replace('#', '') as TabValue
    return VALID_TABS.includes(hash) ? hash : 'settings'
  }, [location.hash])

  const handleTabChange = (value: string) => {
    navigate(`#${value}`, { replace: true })
  }
  
  const group = groups.find((g: { id: string }) => g.id === id)
  const groupUrl = `${window.location.origin}/group/${id}`

  // Set page title
  useDocumentHead({
    title: group ? `Manage ${group.name}` : 'Group Admin',
    description: group ? `Admin settings for ${group.name}` : undefined,
  })

  const currentUserId = profile?.userId
  const currentUserRole = useMemo(() => {
    if (!currentUserId) return null
    const me = members.find((m) => m.userId === currentUserId)
    return me?.role || null
  }, [currentUserId, members])

  const canRemoveMember = useCallback((member: GroupMember) => {
    if (!currentUserId) return false
    if (member.userId === currentUserId) return false
    if (currentUserRole === 'owner') return member.role !== 'owner'
    if (currentUserRole === 'admin') return member.role === 'member'
    return false
  }, [currentUserId, currentUserRole])

  const canChangeRole = useCallback((member: GroupMember) => {
    if (currentUserRole !== 'owner') return false
    if (member.userId === currentUserId) return false
    if (member.role === 'owner') return false
    return true
  }, [currentUserId, currentUserRole])

  const handleRoleChange = async (member: GroupMember, newRole: 'member' | 'admin') => {
    if (!id || newRole === member.role) return
    setUpdatingRoleId(member.userId)
    setMembersError(null)
    try {
      await groupService.updateMember(id, member.userId, { role: newRole })
      setMembers(prev => prev.map(m => 
        m.userId === member.userId ? { ...m, role: newRole } : m
      ))
    } catch (e) {
      console.error('Failed to update role:', e)
      const err = e as { message?: string }
      setMembersError(err?.message || 'Failed to update role')
    } finally {
      setUpdatingRoleId(null)
    }
  }

  const handleOrganiserChange = async (member: GroupMember, isOrganiser: boolean) => {
    if (!id) return
    setUpdatingOrganiserId(member.userId)
    setMembersError(null)
    try {
      await groupService.updateMember(id, member.userId, { isOrganiser })
      setMembers(prev => prev.map(m => 
        m.userId === member.userId ? { ...m, isOrganiser } : m
      ))
    } catch (e) {
      console.error('Failed to update organiser status:', e)
      const err = e as { message?: string }
      setMembersError(err?.message || 'Failed to update organiser status')
    } finally {
      setUpdatingOrganiserId(null)
    }
  }

  const handleRemoveMember = async () => {
    if (!id || !removeMemberDialog) return
    const member = removeMemberDialog
    setRemovingMemberId(member.userId)
    setRemoveMemberError(null)
    try {
      await groupService.removeMember(id, member.userId)
      const updated = await groupService.listMembers(id)
      setMembers(updated)
      setRemoveMemberDialog(null)
      refetch()
    } catch (e) {
      console.error('Failed to remove member:', e)
      const err = e as { message?: string }
      setRemoveMemberError(err?.message || 'Failed to remove member')
    } finally {
      setRemovingMemberId(null)
    }
  }
  
  // Initialize settings form when group loads
  useEffect(() => {
    if (group) {
      setSettingsName(group.name)
      setSettingsIcon(group.icon || null)
      setSettingsGroupType(group.type || 'club')
      setSettingsShowOrganisers(group.showOrganisers ?? true)
      setSettingsIsPublic(group.isPublic ?? false)
      setSettingsIsFeatured(group.isFeatured ?? false)
      setSettingsAllowJoinRequests(group.allowJoinRequests ?? true)
      setSettingsBankDetails(group.bankDetails || {})
      setHasNewIcon(false)
    }
  }, [group])
  
  const handleIconFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const MAX_FILE_SIZE = 2.5 * 1024 * 1024
    if (file.size > MAX_FILE_SIZE) {
      setSettingsError(`File size must be less than 2.5MB`)
      return
    }
    
    if (!file.type.startsWith('image/')) {
      setSettingsError('Please select an image file')
      return
    }
    
    setSettingsError(null)
    
    try {
      const dataUrl = await imageFileToSquarePngDataUrl(file, 256)
      setSettingsIcon(dataUrl)
      setHasNewIcon(true)
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to process image')
    }
  }
  
  const handleRemoveIcon = () => {
    setSettingsIcon(null)
    setHasNewIcon(true)
  }
  
  const handleSettingsSave = async () => {
    if (!id) return
    
    setSettingsSaving(true)
    setSettingsError(null)
    setSettingsSuccess(false)
    
    try {
      const effectiveIsFeatured = settingsIsPublic ? settingsIsFeatured : false
      
      await groupService.update(id, {
        name: settingsName.trim(),
        icon: hasNewIcon ? (settingsIcon || '') : undefined,
        type: settingsGroupType,
        showOrganisers: settingsShowOrganisers,
        isPublic: settingsIsPublic,
        isFeatured: effectiveIsFeatured,
        allowJoinRequests: settingsAllowJoinRequests,
      })
      setHasNewIcon(false)
      setSettingsSuccess(true)
      refetch()
      setTimeout(() => setSettingsSuccess(false), 3000)
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSettingsSaving(false)
    }
  }
  
  const handlePaymentsSave = async () => {
    if (!id) return
    
    setPaymentsSaving(true)
    setPaymentsError(null)
    setPaymentsSuccess(false)
    
    try {
      // Only include bank details if something is filled in
      const hasBankDetails = Object.values(settingsBankDetails).some(v => v && v.trim())
      
      await groupService.update(id, {
        bankDetails: hasBankDetails ? settingsBankDetails : undefined,
      })
      setPaymentsSuccess(true)
      refetch()
      setTimeout(() => setPaymentsSuccess(false), 3000)
    } catch (err) {
      setPaymentsError(err instanceof Error ? err.message : 'Failed to save payment details')
    } finally {
      setPaymentsSaving(false)
    }
  }
  
  // Check if bank details have changed
  const bankDetailsChanged = group && (
    JSON.stringify(settingsBankDetails) !== JSON.stringify(group.bankDetails || {})
  )

  const hasSettingsChanges = group && (
    settingsName !== group.name ||
    hasNewIcon ||
    settingsGroupType !== (group.type || 'club') ||
    settingsShowOrganisers !== (group.showOrganisers ?? true) ||
    settingsIsPublic !== (group.isPublic ?? false) ||
    settingsIsFeatured !== (group.isFeatured ?? false) ||
    settingsAllowJoinRequests !== (group.allowJoinRequests ?? true)
  )

  const hasPaymentChanges = !!bankDetailsChanged

  const handleCopyGroupUrl = async () => {
    try {
      await navigator.clipboard.writeText(groupUrl)
      setUrlCopied(true)
      setTimeout(() => setUrlCopied(false), 1500)
    } catch (e) {
      console.error('Failed to copy group URL:', e)
      setSettingsError('Failed to copy URL')
    }
  }

  const handleDeleteGroup = async () => {
    if (!id || !group) return
    if (deleteConfirmText !== group.name) return
    
    setDeleteLoading(true)
    setDeleteError(null)
    
    try {
      await groupService.delete(id)
      navigate('/groups')
    } catch (err) {
      console.error('Failed to delete group:', err)
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete group')
    } finally {
      setDeleteLoading(false)
    }
  }
  
  const isOwner = currentUserRole === 'owner'

  // League form handlers
  const openCreateLeagueDialog = () => {
    setLeagueFormName('')
    setLeagueFormFormat('singles')
    setLeagueFormMatchFormat('bestOf1')
    setLeagueFormDescription('')
    setLeagueFormError(null)
    setCreateLeagueDialogOpen(true)
  }

  const handleLeagueFormSubmit = async () => {
    if (!id) return
    if (!leagueFormName.trim()) {
      setLeagueFormError('League name is required')
      return
    }

    setLeagueFormSaving(true)
    setLeagueFormError(null)

    try {
      await leagueService.create(id, {
        name: leagueFormName.trim(),
        format: leagueFormFormat,
        matchFormat: leagueFormMatchFormat,
        description: leagueFormDescription.trim() || undefined,
      })
      setCreateLeagueDialogOpen(false)
      refetchLeagues()
    } catch (err) {
      setLeagueFormError(err instanceof Error ? err.message : 'Failed to create league')
    } finally {
      setLeagueFormSaving(false)
    }
  }

  // Invite handlers
  const handleCreateInvite = async () => {
    if (!id) return
    setCreateInviteLoading(true)
    setInvitesError(null)
    setCreatedInviteLink(null)
    try {
      const created = await groupService.createInvite(id, {
        email: createInviteEmail.trim() || undefined,
        role: createInviteRole,
      })
      const link = `${window.location.origin}${created.acceptPath || `/invite/${created.token}`}`
      setCreatedInviteLink(link)
      setCreateInviteEmail('')
      const updated = await groupService.listInvites(id)
      setInvites(updated)
    } catch (err) {
      console.error('Failed to create invite:', err)
      setInvitesError(err instanceof Error ? err.message : 'Failed to create invite')
    } finally {
      setCreateInviteLoading(false)
    }
  }

  const handleCopyCreatedLink = async () => {
    if (!createdInviteLink) return
    try {
      await navigator.clipboard.writeText(createdInviteLink)
      setInviteLinkCopied(true)
      setTimeout(() => setInviteLinkCopied(false), 1500)
    } catch (e) {
      console.error('Failed to copy invite link:', e)
    }
  }

  const handleCopyInviteLink = async (invite: GroupInvite) => {
    const link = `${window.location.origin}/invite/${invite.token}`
    try {
      await navigator.clipboard.writeText(link)
      setCopiedInviteId(invite.inviteId)
      setTimeout(() => setCopiedInviteId(null), 1500)
    } catch (e) {
      console.error('Failed to copy invite link:', e)
      setInvitesError('Failed to copy invite link')
    }
  }

  const handleRevokeInvite = async (invite: GroupInvite) => {
    if (!id) return
    try {
      await groupService.revokeInvite(id, invite.inviteId)
      const updated = await groupService.listInvites(id)
      setInvites(updated)
    } catch (e) {
      console.error('Failed to revoke invite:', e)
      setInvitesError('Failed to revoke invite')
    }
  }

  // Load members
  useEffect(() => {
    async function fetchMembers() {
      if (!id || !group?.isAdmin) return
      
      setMembersLoading(true)
      setMembersError(null)
      
      try {
        const data = await groupService.listMembers(id)
        setMembers(data)
      } catch (err) {
        console.error('Failed to load members:', err)
        setMembersError('Failed to load members')
      } finally {
        setMembersLoading(false)
      }
    }
    
    fetchMembers()
  }, [id, group?.isAdmin])

  // Load invites
  useEffect(() => {
    async function fetchInvites() {
      if (!id || !group?.isAdmin) return
      setInvitesLoading(true)
      setInvitesError(null)
      try {
        const data = await groupService.listInvites(id)
        setInvites(data)
      } catch (err) {
        console.error('Failed to load invites:', err)
        setInvitesError('Failed to load invites')
      } finally {
        setInvitesLoading(false)
      }
    }
    fetchInvites()
  }, [id, group?.isAdmin])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    )
  }

  if (!group || !group.isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to manage this group.
            </p>
            <Button onClick={() => navigate('/groups')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to My Groups
            </Button>
          </Card>
        </main>
      </div>
    )
  }

  const pendingInvites = invites.filter(i => i.status === 'pending').length
  const activeLeagues = leagues.filter((l: { status: string }) => l.status === 'active').length

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/group/${id}`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Group
        </Button>

        <div className="flex items-center gap-4 mb-8">
          <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center text-3xl overflow-hidden">
            {group.icon?.startsWith('data:') || group.icon?.startsWith('https://') ? (
              <img src={group.icon} alt={group.name} className="h-full w-full object-cover" />
            ) : (
              group.icon || '🏸'
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Manage {group.name}
            </h1>
            <p className="text-muted-foreground">Configure group settings and manage members</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="w-full mb-6 flex-wrap h-auto">
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="leagues" className="gap-2">
              <Trophy className="h-4 w-4" />
              Leagues
              {activeLeagues > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                  {activeLeagues}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="members" className="gap-2">
              <Users className="h-4 w-4" />
              Members
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                {group.memberCount}
              </span>
            </TabsTrigger>
            <TabsTrigger value="invites" className="gap-2">
              <Mail className="h-4 w-4" />
              Invites
              {pendingInvites > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                  {pendingInvites}
                </span>
              )}
            </TabsTrigger>
            {group.isPublic && (
              <TabsTrigger value="requests" className="gap-2">
                <UserPlus className="h-4 w-4" />
                Join Requests
              </TabsTrigger>
            )}
            <TabsTrigger value="payments" className="gap-2">
              <Landmark className="h-4 w-4" />
              Payments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings">
            <SettingsTab
              group={group}
              name={settingsName}
              onNameChange={setSettingsName}
              icon={settingsIcon}
              onIconSelect={handleIconFileSelect}
              onRemoveIcon={handleRemoveIcon}
              groupType={settingsGroupType}
              onGroupTypeChange={setSettingsGroupType}
              showOrganisers={settingsShowOrganisers}
              onShowOrganisersChange={setSettingsShowOrganisers}
              isPublic={settingsIsPublic}
              onIsPublicChange={setSettingsIsPublic}
              isFeatured={settingsIsFeatured}
              onIsFeaturedChange={setSettingsIsFeatured}
              allowJoinRequests={settingsAllowJoinRequests}
              onAllowJoinRequestsChange={setSettingsAllowJoinRequests}
              hasChanges={!!hasSettingsChanges}
              saving={settingsSaving}
              error={settingsError}
              success={settingsSuccess}
              urlCopied={urlCopied}
              onCopyUrl={handleCopyGroupUrl}
              onSave={handleSettingsSave}
              isOwner={isOwner}
              onOpenDeleteDialog={() => {
                setDeleteConfirmText('')
                setDeleteError(null)
                setDeleteDialogOpen(true)
              }}
              groupUrl={groupUrl}
            />
          </TabsContent>

          <TabsContent value="leagues">
            <LeaguesTab
              leagues={leagues}
              leaguesLoading={leaguesLoading}
              leaguesError={leaguesError}
              onCreateLeague={openCreateLeagueDialog}
              onManageLeague={(leagueId) => navigate(`/group/${id}/admin/league/${leagueId}`, { state: { from: 'group-admin' } })}
            />
          </TabsContent>

          <TabsContent value="members">
            <MembersTab
              members={members}
              membersLoading={membersLoading}
              membersError={membersError}
              canRemoveMember={canRemoveMember}
              canChangeRole={canChangeRole}
              onRoleChange={handleRoleChange}
              onOrganiserChange={handleOrganiserChange}
              onRemoveMember={(member) => setRemoveMemberDialog(member)}
              removingMemberId={removingMemberId}
              updatingRoleId={updatingRoleId}
              updatingOrganiserId={updatingOrganiserId}
            />
          </TabsContent>

          <TabsContent value="invites">
            <InvitesTab
              invites={invites}
              invitesLoading={invitesLoading}
              invitesError={invitesError}
              createEmail={createInviteEmail}
              onCreateEmailChange={setCreateInviteEmail}
              createRole={createInviteRole}
              onCreateRoleChange={setCreateInviteRole}
              createLoading={createInviteLoading}
              createdLink={createdInviteLink}
              linkCopied={inviteLinkCopied}
              onCreateInvite={handleCreateInvite}
              onCopyCreatedLink={handleCopyCreatedLink}
              copiedInviteId={copiedInviteId}
              onCopyInviteLink={handleCopyInviteLink}
              onRevokeInvite={handleRevokeInvite}
            />
          </TabsContent>

          {group.isPublic && (
            <TabsContent value="requests">
              <JoinRequestsTab
                groupId={group.id}
                onMemberAdded={async () => {
                  // Refresh members list
                  try {
                    const data = await groupService.listMembers(group.id)
                    setMembers(data)
                  } catch (err) {
                    console.error('Failed to refresh members:', err)
                  }
                }}
              />
            </TabsContent>
          )}

          <TabsContent value="payments">
            <PaymentsTab
              bankDetails={settingsBankDetails}
              onBankDetailsChange={(field, value) =>
                setSettingsBankDetails(prev => ({ ...prev, [field]: value }))
              }
              hasChanges={hasPaymentChanges}
              saving={paymentsSaving}
              error={paymentsError}
              success={paymentsSuccess}
              onSave={handlePaymentsSave}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialogs */}
      <CreateLeagueDialog
        open={createLeagueDialogOpen}
        onOpenChange={setCreateLeagueDialogOpen}
        name={leagueFormName}
        onNameChange={setLeagueFormName}
        format={leagueFormFormat}
        onFormatChange={setLeagueFormFormat}
        matchFormat={leagueFormMatchFormat}
        onMatchFormatChange={setLeagueFormMatchFormat}
        description={leagueFormDescription}
        onDescriptionChange={setLeagueFormDescription}
        onSubmit={handleLeagueFormSubmit}
        saving={leagueFormSaving}
        error={leagueFormError}
      />

      <DeleteGroupDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        groupName={group?.name || ''}
        confirmText={deleteConfirmText}
        onConfirmTextChange={setDeleteConfirmText}
        onDelete={handleDeleteGroup}
        deleting={deleteLoading}
        error={deleteError}
      />

      {/* Remove Member Confirmation */}
      {removeMemberDialog && (
        <ConfirmDeleteDialog
          open={!!removeMemberDialog}
          onOpenChange={(open) => {
            if (!open) {
              setRemoveMemberDialog(null)
              setRemoveMemberError(null)
            }
          }}
          title="Remove Member"
          description={`Remove ${removeMemberDialog.displayName} from this group? They will lose access to all leagues and matches.`}
          confirmText={removeMemberDialog.displayName}
          onConfirm={handleRemoveMember}
          loading={removingMemberId === removeMemberDialog.userId}
          error={removeMemberError}
        />
      )}
    </div>
  )
}
